import type { ScaleCode } from './scales';

export const ASSESSMENT_KINDS = ['current', 'test', 'thematic', 'semester', 'year'] as const;
export type AssessmentKind = (typeof ASSESSMENT_KINDS)[number];

/** How the system suggests a final grade. The teacher always makes the final decision. */
export type FinalMethod = 'teacher' | 'mean' | 'median' | 'weighted';
export type Rounding = 'half_up' | 'floor' | 'ceil';

/**
 * A grading rule is configuration, not code: МОН changes the rules almost every year
 * (orders №722 and №1427 in 2026), so schools and new academic years get new rows.
 */
export interface GradingRule {
  id: string;
  /** e.g. '2026/2027'; undefined = any year */
  academicYear?: string;
  gradeFrom: number;
  gradeTo: number;
  subjectId?: string;
  /** undefined = any assessment kind */
  assessmentKind?: AssessmentKind;
  scale: ScaleCode;
  finalMethod?: FinalMethod;
  rounding?: Rounding;
  /** school override (decision of the pedagogical council); undefined = national default */
  schoolId?: string;
  /** tie-breaker among equally specific rules; higher wins */
  priority?: number;
}

export interface RuleContext {
  academicYear: string;
  grade: number;
  kind: AssessmentKind;
  subjectId?: string;
  schoolId?: string;
}

function matches(rule: GradingRule, ctx: RuleContext): boolean {
  if (rule.academicYear !== undefined && rule.academicYear !== ctx.academicYear) return false;
  if (ctx.grade < rule.gradeFrom || ctx.grade > rule.gradeTo) return false;
  if (rule.assessmentKind !== undefined && rule.assessmentKind !== ctx.kind) return false;
  if (rule.subjectId !== undefined && rule.subjectId !== ctx.subjectId) return false;
  if (rule.schoolId !== undefined && rule.schoolId !== ctx.schoolId) return false;
  return true;
}

function specificity(rule: GradingRule): number[] {
  return [
    rule.schoolId !== undefined ? 1 : 0,
    rule.subjectId !== undefined ? 1 : 0,
    rule.assessmentKind !== undefined ? 1 : 0,
    rule.academicYear !== undefined ? 1 : 0,
    -(rule.gradeTo - rule.gradeFrom), // narrower grade range wins
    rule.priority ?? 0,
  ];
}

function compareDesc(a: number[], b: number[]): number {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const diff = (b[i] ?? 0) - (a[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/** Returns the most specific rule for the context, or undefined if nothing matches. */
export function resolveRule(
  rules: ReadonlyArray<GradingRule>,
  ctx: RuleContext,
): GradingRule | undefined {
  return rules
    .filter((rule) => matches(rule, ctx))
    .sort((a, b) => compareDesc(specificity(a), specificity(b)))[0];
}

/**
 * National defaults for 2026/27:
 * - order №722 for grades 1–9: grades 1–2 verbal only; grades 3–4 current verbal, final by levels;
 *   grades 5–9 points (order №1427: semester grade derived from thematic ones);
 * - order №329 (12-point scale) for grades 10–11 until 01.07.2028.
 * Formative feedback is never a grade and lives in a separate table.
 */
export const NATIONAL_RULES_2026: ReadonlyArray<GradingRule> = [
  {
    id: 'ua-2026-g1-2',
    academicYear: '2026/2027',
    gradeFrom: 1,
    gradeTo: 2,
    scale: 'verbal',
    finalMethod: 'teacher',
  },
  {
    id: 'ua-2026-g3-4-current',
    academicYear: '2026/2027',
    gradeFrom: 3,
    gradeTo: 4,
    assessmentKind: 'current',
    scale: 'verbal',
  },
  {
    id: 'ua-2026-g3-4-test',
    academicYear: '2026/2027',
    gradeFrom: 3,
    gradeTo: 4,
    assessmentKind: 'test',
    scale: 'levels4',
  },
  {
    id: 'ua-2026-g3-4-thematic',
    academicYear: '2026/2027',
    gradeFrom: 3,
    gradeTo: 4,
    assessmentKind: 'thematic',
    scale: 'levels4',
    finalMethod: 'mean',
    rounding: 'half_up',
  },
  {
    id: 'ua-2026-g3-4-semester',
    academicYear: '2026/2027',
    gradeFrom: 3,
    gradeTo: 4,
    assessmentKind: 'semester',
    scale: 'levels4',
    finalMethod: 'mean',
    rounding: 'half_up',
  },
  {
    id: 'ua-2026-g3-4-year',
    academicYear: '2026/2027',
    gradeFrom: 3,
    gradeTo: 4,
    assessmentKind: 'year',
    scale: 'levels4',
    finalMethod: 'mean',
    rounding: 'half_up',
  },
  {
    id: 'ua-2026-g5-9',
    academicYear: '2026/2027',
    gradeFrom: 5,
    gradeTo: 9,
    scale: 'points12',
    finalMethod: 'mean',
    rounding: 'half_up',
  },
  {
    id: 'ua-2026-g10-11',
    academicYear: '2026/2027',
    gradeFrom: 10,
    gradeTo: 11,
    scale: 'points12',
    finalMethod: 'mean',
    rounding: 'half_up',
  },
];
